import { CepInput } from '@/_components/inputs/cep-input'
import { CnpjInput } from '@/_components/inputs/cnpj-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import { InputGroup } from '@/features/_components/input-group'
import { ApiQueryError, mutations } from '@/features/_lib/react-query'
import {
  CreateFacilityInputDTOSchema,
  type TCreateFacilityInputDTO,
} from '@doutorsim/shared/facilities'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import {
  Button,
  DialogClose,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../_components/ui'
import { Text, Title } from '../../_components/ui/typography'
import { BRAZILIAN_STATES } from '../../_constants/brazilian-states'

interface CreateFacilityDialogProps {
  companyId: string
  setIsDialogOpen: (isOpen: boolean) => void
}

export const CreateFacilityDialog = ({ companyId, setIsDialogOpen }: CreateFacilityDialogProps) => {
  const queryClient = useQueryClient()
  const staffCreateFacility = useMutation(mutations.staffFacilities.create(queryClient))

  const form = useForm<TCreateFacilityInputDTO>({
    resolver: zodResolver(CreateFacilityInputDTOSchema),
    shouldUnregister: false,
    defaultValues: {
      companyId: companyId,
      address: {
        countryId: 'BR',
        premise: null,
        poBox: null,
        coordinates: null,
      },
      contactInfo: {
        phone: null,
        email: null,
      },
    },
  })

  const onSubmit = (data: TCreateFacilityInputDTO) => {
    staffCreateFacility.mutate(data, {
      onSuccess: () => {
        toast.success('Cadastro efetuado!', {
          description: 'A unidade foi criada com sucesso!',
        })
        setIsDialogOpen(false)
      },
      onError: (error) => {
        toast.error('Erro ao cadastrar unidade.', {
          description:
            error instanceof ApiQueryError
              ? (error.apiError.detail ?? error.message)
              : 'Erro inesperado',
        })
      },
    })
  }

  return (
    <Form {...form}>
      <div className="flex flex-col items-center justify-center gap-1">
        <Title>Cadastro de unidade</Title>
        <Text>Insira as informações abaixo</Text>
      </div>
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold">Nome Fantasia</FormLabel>
              <FormControl>
                <Input placeholder="Digite o nome fantasia da unidade" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <InputGroup>
          <FormField
            control={form.control}
            name="legalName"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Razão Social</FormLabel>
                <FormControl>
                  <Input placeholder="Digite a razão social da unidade" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cnpj"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">CNPJ</FormLabel>
                <FormControl>
                  <CnpjInput placeholder="Digite o CNPJ da unidade" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>

        <InputGroup>
          <FormField
            control={form.control}
            name="contactInfo.phone"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Telefone</FormLabel>
                <FormControl>
                  <PhoneInput {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="contactInfo.email"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">E-mail</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Digite o e-mail da unidade"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>

        <InputGroup>
          <FormField
            control={form.control}
            name="address.postcode"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">CEP</FormLabel>
                <FormControl>
                  <CepInput placeholder="CEP" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address.thoroughfare"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-[2]">
                <FormLabel className="text-sm font-semibold">Logradouro</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Logradouro (Rua/Avenida)"
                    {...field}
                    value={field.value ?? ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address.premiseNumber"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Número</FormLabel>
                <FormControl>
                  <Input placeholder="Digite o número" {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>

        <InputGroup>
          <FormField
            control={form.control}
            name="address.subPremise"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Complemento</FormLabel>
                <FormControl>
                  <Input placeholder="Digite o complemento" {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address.locality"
            render={({ field }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Cidade</FormLabel>
                <FormControl>
                  <Input placeholder="Digite a cidade" {...field} value={field.value ?? ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="address.subdivisionId"
            render={({ field: { value, onChange } }) => (
              <FormItem className="w-full lg:flex-1">
                <FormLabel className="text-sm font-semibold">Estado</FormLabel>
                <FormControl>
                  <Select value={value!} onValueChange={onChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Selecione o estado" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAZILIAN_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </InputGroup>

        <div className="mt-6 flex w-full justify-center gap-3">
          <DialogClose asChild>
            <Button
              variant="ghost"
              className="font-bold hover:bg-transparent hover:text-brand-grayscale-12"
              type="reset"
            >
              Cancelar
            </Button>
          </DialogClose>
          <Button
            type="submit"
            disabled={staffCreateFacility.isPending}
            className="w-[142px] font-bold"
          >
            {staffCreateFacility.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cadastrar
          </Button>
        </div>
      </form>
    </Form>
  )
}
